/**
 * Log Center (`/logs`) — unified logging hub.
 *
 * Consolidates the previously-separate Audit Log (`/audit-log`) and Meta API
 * activity (`/meta-activity`) pages into one tabbed surface:
 *   - "Audit"     → org activity trail (who did what)        → <AuditLog embedded />
 *   - "Meta API"  → every Meta Graph call + rate-limit usage → <MetaActivity embedded />
 *   - "MCP Calls" → every MCP tool call (reads + writes)     → <McpCalls embedded />
 *
 * Each tab is rendered by the existing page component in `embedded` mode, so it
 * draws only its inner content while this page owns the PageWrapperMain/SubPage
 * chrome + the page title. New sources (Executions, Notifications, …) drop in as
 * additional tabs later.
 *
 * Tabs are individually gated (owner and manager by default) via the `audit.view` /
 * `meta_activity.view` / `mcp_calls.view` capabilities — a tab only shows if its
 * capability is granted. The active tab is reflected in `?tab=` so deep links +
 * the legacy route redirects (`/audit-log` → `/logs?tab=audit`) land on the
 * right tab.
 *
 * Shell matches the rest of the agency surface:
 *   PageWrapperMain → subPage → SubPageContentHolder → SubPageInnerContentHolder.
 * Plain divs + custom SCSS (`log-center/logCenter.scss`) so it picks up the
 * legacy theme variables. No MUI, no chart library.
 */
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import '../../styles/pages/log-center/logCenter.scss';

import PageWrapperMain from '../../components/page-wrappers/PageWrapperMain';
import SubPageContentHolder from '../../components/subPageContentHolder/SubPageContentHolder';
import SubPageInnerContentHolder from '../../components/subPageContentHolder/SubPageInnerContentHolder';
import TabErrorBoundary from '../../components/errorBoundary/TabErrorBoundary';
import { useCan } from '../../lib/rbac';
import AuditLog from '../auditLog/AuditLog';
import MetaActivity from '../metaActivity/MetaActivity';
import McpCalls from '../mcpCalls/McpCalls';

type LogTab = 'audit' | 'meta' | 'mcp';

const LogCenter: React.FC = () => {
  const { t } = useTranslation();
  const canAudit = useCan('audit.view');
  const canMeta = useCan('meta_activity.view');
  const canMcp = useCan('mcp_calls.view');

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');

  // Which tabs this user may see, in display order.
  const tabs = useMemo(
    () =>
      (
        [
          { key: 'audit', label: t('LogCenterTabAudit'), show: canAudit },
          { key: 'meta', label: t('LogCenterTabMeta'), show: canMeta },
          { key: 'mcp', label: t('LogCenterTabMcp'), show: canMcp },
        ] as Array<{ key: LogTab; label: string; show: boolean }>
      ).filter((tab) => tab.show),
    [t, canAudit, canMeta, canMcp]
  );

  // Resolve the initial active tab: honour `?tab=` when allowed, else first
  // available tab. Computed once on mount (the user drives it after that).
  const [active, setActive] = useState<LogTab>(() => {
    if (requestedTab === 'mcp' && canMcp) return 'mcp';
    if (requestedTab === 'meta' && canMeta) return 'meta';
    if (requestedTab === 'audit' && canAudit) return 'audit';
    return (tabs[0]?.key ?? 'audit') as LogTab;
  });

  const selectTab = (key: LogTab) => {
    setActive(key);
    // Keep the URL in sync so refresh / deep-link / back lands on this tab.
    setSearchParams({ tab: key }, { replace: true });
  };

  return (
    <PageWrapperMain>
      <div className='subPage'>
        <SubPageContentHolder>
          <SubPageInnerContentHolder
            title={t('LogCenter')}
            subTitle={t('LogCenterSubTitle')}
            backgroundColor={'var(--main-background-color)'}
          >
            <div className='logCenterPage'>
              {tabs.length === 0 ? (
                <div className='logCenterPage__noAccess'>
                  {t('LogCenterNoAccess')}
                </div>
              ) : (
                <>
                  {/* ---------- Source tabs ---------- */}
                  <div className='logCenterTabs' role='tablist'>
                    {tabs.map((tab) => (
                      <button
                        key={tab.key}
                        type='button'
                        role='tab'
                        aria-selected={active === tab.key}
                        className={`logCenterTabs__tab ${
                          active === tab.key ? 'logCenterTabs__tab--active' : ''
                        }`}
                        onClick={() => selectTab(tab.key)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* ---------- Active source body ----------
                       Wrapped in a local error boundary so a crash in ONE tab
                       can never white-screen the whole Log Center — the tabs +
                       chrome stay usable and the actual error is shown in-place.
                       Keyed by `active` so switching tabs retries cleanly. */}
                  <div className='logCenterBody'>
                    <TabErrorBoundary
                      resetKey={active}
                      label={t('LogCenterTabError', {
                        defaultValue:
                          'This log view ran into an error and could not load.',
                      })}
                    >
                      {active === 'audit' && canAudit && <AuditLog embedded />}
                      {active === 'meta' && canMeta && <MetaActivity embedded />}
                      {active === 'mcp' && canMcp && <McpCalls embedded />}
                    </TabErrorBoundary>
                  </div>
                </>
              )}
            </div>
          </SubPageInnerContentHolder>
        </SubPageContentHolder>
      </div>
    </PageWrapperMain>
  );
};

export default LogCenter;
